import React, { useState, useEffect } from 'react';
import { 
  getHandlerRequestsByHandlerId,
  SaveParticipant,
} from '../api/actions';
import { getLoggedInParticipant } from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';

export default function HandlerInfoCard({ 
  editedData = {},
  participant, 
  onMessage, 
  onParticipantUpdate,
  isEditing,
  onEditingChange,
  saving,
  setSaving,
  onChange, 
}) {
  const [handlersRequestsReceived, setHandlersRequestsReceived] = useState([]);
  const [loading, setLoading] = useState(false);
  const { participants: allParticipants } = useParticipants();


  useEffect(() => {
    if (participant && hasRole('handler')) {
      loadHandlerRequests();
    }
  }, [participant]);

  // Register save handler with parent component
  useEffect(() => {
    if (isEditing) {
      window.handlerInfoCardSave = handleSave;
    }
    return () => {
      delete window.handlerInfoCardSave;
    };
  }, [isEditing, editedData]);

  const hasRole = (roleToCheck) => {
    if (Array.isArray(participant?.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant?.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  const formatSection = (section) => {
    if (!section || typeof section !== 'string') return 'N/A';
    const s = section.trim().toLowerCase();
    if (s === 'north') return 'North';
    if (s === 'central') return 'Central';
    if (s === 'south') return 'South';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getDistanceValue = (handlerInfo) => {
    return handlerInfo?.distanceFromRaceStation || 
           handlerInfo?.distance || 
           handlerInfo?.distanceFromLiberation || 
           handlerInfo?.distanceMiles || 
           handlerInfo?.dist || 
           '';
  };

  const loadHandlerRequests = async () => {
    try {
      setLoading(true);
      const handlerResult = await getHandlerRequestsByHandlerId(participant.id);
      if (Array.isArray(handlerResult)) {
        setHandlersRequestsReceived(handlerResult);
      } else if (handlerResult && Array.isArray(handlerResult.requests)) {
        setHandlersRequestsReceived(handlerResult.requests);
      } else {
        setHandlersRequestsReceived([]);
      }
    } catch (error) {
      setHandlersRequestsReceived([]);
      if (onMessage) {
        onMessage('Failed to load handler requests. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedData || saving) return;

    // Validate mobile number if Call First is enabled
    if (editedData.handlerInfo?.preferCallFirst && !participant.mobile) {
      if (onMessage) {
        onMessage('Mobile number is required when "Call First" preference is enabled. Please add your mobile number in Personal Information.');
      }
      return; // Don't save
    }

    try {
      if (setSaving) {
        setSaving(true);
      }

      const authData = getLoggedInParticipant();
      if (!authData?.participantId) {
        throw new Error('Authentication required');
      }

      const result = await SaveParticipant(editedData);
      
      if (result.success) {
        if (onMessage) {
          onMessage('Handler information updated successfully!');
        }
        if (onParticipantUpdate) {
          await onParticipantUpdate();
        }
        if (onEditingChange) {
          onEditingChange(false);
        }
      } else {
        throw new Error(result.message || 'Failed to save handler information');
      }
    } catch (error) {
      if (onMessage) {
        onMessage('An error occurred while saving handler information.');
      }
      if (onEditingChange) {
        onEditingChange(true);
      }
    } finally {
      if (setSaving) {
        setSaving(false);
      }
    }
  };

  const handleContactMethodChange = (method, checked) => {
    const currentMethods = editedData.handlerInfo?.breederApplicationMethod || [];
    const newMethods = checked 
      ? [...currentMethods.filter(m => m !== method), method]
      : currentMethods.filter(m => m !== method);

  };



  if (!participant || !hasRole('handler')) {
    return null;
  }

  return (
    <>
      {/* Alert for missing required handler fields */}
      {participant && (!participant.handlerInfo?.section || !participant.handlerInfo?.totalBirds || participant.handlerInfo?.totalBirds === 0) && (
        <div className="alert alert-warning mb-4 shadow-sm" role="alert">
          <h6 className="alert-heading">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Action Required: Complete Your Handler Profile
          </h6>
          <p className="mb-2">Please update the following required fields to accept breeder requests:</p>
          <ul className="mb-2">
            {!participant.handlerInfo?.section && (
              <li><strong>Section</strong> - Select your race section (North, Central, or South)</li>
            )}
            {(!participant.handlerInfo?.totalBirds || participant.handlerInfo?.totalBirds === 0) && (
              <li><strong>Total Birds Capacity</strong> - Specify how many birds you can accept for this race</li>
            )}
          </ul>
          {!isEditing && (
            <div className="alert alert-info mt-3">
              <i className="bi bi-info-circle me-2"></i>
              To edit handler settings, click "Edit Profile" above
            </div>
          )}
        </div>
      )}
      
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-flag me-2"></i>
            Handler Information
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label fw-bold">Section</label>
              {isEditing ? (
                <select
                  className="form-select"
                  value={(editedData.handlerInfo?.section || '').toLowerCase()}
                  onChange={e => onChange && onChange('handlerInfo', { ...editedData.handlerInfo, section: e.target.value })}                 

                >
                  <option value="">Select Section</option>
                  <option value="north">North</option>
                  <option value="central">Central</option>
                  <option value="south">South</option>
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={formatSection(participant.handlerInfo?.section) || 'Not assigned'}
                  disabled
                />
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Total Birds Capacity</label>
              <input
                type="text"
                className="form-control"
                value={isEditing ? (editedData.handlerInfo?.totalBirds || 0) : (participant.handlerInfo?.totalBirds || '0')}
                onChange={e => onChange && onChange('handlerInfo', { ...editedData.handlerInfo, totalBirds: e.target.value })}                
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Distance (miles)</label>
              <input
                type="number"
                className="form-control"
                value={editedData?.handlerInfo?.distanceFromRaceStation ||''}
                placeholder="Distance from race station"                
                onChange={e => onChange && onChange('handlerInfo', { ...editedData.handlerInfo, distanceFromRaceStation: e.target.value })}                
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">Availability Status</label>
              <div className="pt-2">
                {(() => {
                  const totalBirds = participant.handlerInfo?.totalBirds;
                  // Calculate approved birds: sum of all approved handler requests
                  let approvedBirds = 0;
                  if (Array.isArray(handlersRequestsReceived)) {
                    approvedBirds = handlersRequestsReceived.reduce((sum, req) => {
                      // Only count requests that are approved
                      if (req.status && req.status.toLowerCase() === 'approved') {
                        const birds = parseInt(req.numberOfBirds || req.birdsRequested) || 0;
                        return sum + birds;
                      }
                      return sum;
                    }, 0);
                  }
                  const openSlots = (totalBirds || 0) - approvedBirds;
                  if (!totalBirds || totalBirds === 0) {
                    return (
                      <span className="badge bg-warning fs-6 px-3 py-2">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Capacity Not Set
                      </span>
                    );
                  }
                  return openSlots > 0 ? (
                    <span className="badge bg-success fs-6 px-3 py-2">{openSlots} Slots Available</span>
                  ) : (
                    <span className="badge bg-secondary fs-6 px-3 py-2">Fully Booked</span>
                  );
                })()}
              </div>
            </div>
            
            {/* Contact Preference */}
            <div className="col-12">
              <label className="form-label fw-bold">Contact Preference</label>
              {isEditing ? (
                <div className="mt-2">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="contactCallFirst" 
                      value="call-first"
                      checked={editedData.handlerInfo?.preferCallFirst || false}
                      onChange={e => {
                        const checked = e.target.checked; // capture synchronously
                        onChange && onChange('handlerInfo', { ...editedData.handlerInfo, preferCallFirst: checked });
                      }}
                    />
                    <label className="form-check-label" htmlFor="contactCallFirst">
                      <i className="bi bi-telephone me-1"></i>I prefer breeders to call me first before sending birds
                    </label>
                  </div>
                  {editedData.handlerInfo?.preferCallFirst && !participant.mobile && (
                    <div className="alert alert-warning mt-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      <strong>Mobile number required:</strong> You must provide a mobile number in your personal information to use this option.
                    </div>
                  )}
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                  {participant.handlerInfo?.breederApplicationMethod?.includes('call') ? (
                    <span className="badge bg-success fs-6 px-3 py-2">
                      <i className="bi bi-telephone me-1"></i>
                      Prefers Call First
                    </span>
                  ) : (
                    <span className="badge bg-primary fs-6 px-3 py-2">
                      <i className="bi bi-app me-1"></i>
                      In App Requests Welcome
                    </span>
                  )}
                </div>
              )}
              {!isEditing && (
                <div className="alert alert-info">
                  <small><i className="bi bi-pencil me-1"></i>Click "Update Information" above to modify contact preferences</small>
                </div>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="mt-3">
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>You have unsaved changes.</strong> Click "Save Changes" above to save your updates.
              </div>
            </div>
          )}

          {/* My Breeders Section */}
          {participant.handlerInfo?.breeders && participant.handlerInfo.breeders.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-bold mb-3">My Breeders ({participant.handlerInfo.breeders.length})</h6>
              <div className="list-group">
                {participant.handlerInfo.breeders.map((breeder, idx) => {
                  const breederParticipant = allParticipants?.find(p => p.id === breeder.breederId);
                  
                  return (
                    <div key={idx} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <strong>{breeder.breederName}</strong>
                          <small className="text-muted ms-2">{breeder.loftName}</small>
                          {breederParticipant && (
                            <div className="small mt-1">
                              {breederParticipant.emailAddress && (
                                <>
                                  <i className="bi bi-envelope me-1"></i>
                                  <a href={`mailto:${breederParticipant.emailAddress}`} className="text-decoration-none me-3">
                                    {breederParticipant.emailAddress}
                                  </a>
                                </>
                              )}
                              {breederParticipant.mobile && (
                                <>
                                  <i className="bi bi-phone me-1"></i>
                                  <a href={`tel:${breederParticipant.mobile}`} className="text-decoration-none">
                                    {breederParticipant.mobile}
                                  </a>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="badge bg-primary">{breeder.birds?.length || 0} birds</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}